// Library Methods
import { useCallback, useMemo } from "react";

// Components
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";

// Constants
import { examCardEnums, ExamCardSectionNameType } from "src/constants/enums";

// Styles
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

// Hooks
import useVFScreeningColumns from "./hooks/useVFScreeningColumns";
import useColorVisionColumns from "./hooks/useColorVisionColumns";
import useVisualAcuityColumns from "./hooks/useVisualAcuityColumns";
import useRAPDColumns from "./hooks/useRAPDColumns";
import useVFEstermanColumns from "./hooks/useVFEstermanColumns";
import useVFThresholdingColumns from "./hooks/useVFThresholdingColumns";
import useContrastSensitivityColumns from "./hooks/useContrastSensitivityColumns";
import useFull120Columns from "./hooks/useFull120Columns";
import useOcularMotilityColumns from "./hooks/useOcularMotilityColumns";
import useSuperior36Columns from "./hooks/useSuperior36Columns";
import useLensSimulationColumns from "./hooks/useLensSimulationColumns";
import useVideoColumns from "./hooks/useVideoColumns";
import useDeviationAndCorrespondenceColumns from "./hooks/useDeviationAndCorrespondenceColumns";
import useFusionAndVergenceColumns from "./hooks/useFusionAndVergenceColumns";
import useStereopsisColumns from "./hooks/useStereopsisColumns";

type ReportColumnsType = {
  sectionName?: ExamCardSectionNameType;
  groupId: number;
};

const ReportColumns = ({ sectionName, groupId }: ReportColumnsType) => {
  const theme = useTheme();
  const smallerThanSm = useMediaQuery(theme.breakpoints.down("sm"));

  const { superior36Columns } = useSuperior36Columns();
  const { full120Columns } = useFull120Columns();
  const { vfScreeningColumns } = useVFScreeningColumns();
  const { vfThresholdingColumns } = useVFThresholdingColumns();
  const { vfEstermanColumns } = useVFEstermanColumns();
  const { colorVisionColumns } = useColorVisionColumns();
  const { visualAcuityColumns } = useVisualAcuityColumns({ groupId });
  const { rapdColumns } = useRAPDColumns();
  const { contrastSensitivityColumns } = useContrastSensitivityColumns();
  const { ocularMotilityColumns } = useOcularMotilityColumns();
  const { lensSimulationColumns } = useLensSimulationColumns();
  const { videoColumns } = useVideoColumns();
  const { deviationCorrespondenceColumns } =
    useDeviationAndCorrespondenceColumns();
  const { fusionVergenceColumns } = useFusionAndVergenceColumns();
  const { stereopsisColumns } = useStereopsisColumns();

  // columns to display on larger screens
  const columns = useMemo(() => {
    switch (sectionName) {
      // video columns
      case examCardEnums.VIDEO.sectionName:
        return videoColumns;

      // lens simulation columns
      case examCardEnums.IOL_SIMULATION.sectionName:
      case examCardEnums.PROGRESSIVE_SIMULATION.sectionName:
        return lensSimulationColumns;

      // visual field columns
      case examCardEnums.VISUAL_FIELD_SCREENING.sectionName:
        return vfScreeningColumns;
      case examCardEnums.VISUAL_FIELD_THRESHOLDING.sectionName:
        return vfThresholdingColumns;
      case examCardEnums.VISUAL_FIELD_ESTERMAN.sectionName:
        return vfEstermanColumns;
      case examCardEnums.VISUAL_FIELD_FULL_120.sectionName:
        return full120Columns;
      case examCardEnums.VISUAL_FIELD_SUPERIOR_36.sectionName:
        return superior36Columns;

      // other columns
      case examCardEnums.DEVIATION_AND_CORRESPONDENCE.sectionName:
        return deviationCorrespondenceColumns;
      case examCardEnums.FUSION_AND_VERGENCE.sectionName:
        return fusionVergenceColumns;
      case examCardEnums.STEREOPSIS.sectionName:
        return stereopsisColumns;
      case examCardEnums.COLOR_VISION.sectionName:
        return colorVisionColumns;
      case examCardEnums.CONTRAST_SENSITIVITY.sectionName:
        return contrastSensitivityColumns;
      case examCardEnums.RAPD.sectionName:
        return rapdColumns;
      case examCardEnums.VISUAL_ACUITY.sectionName:
        return visualAcuityColumns;
      case examCardEnums.OCULAR_MOTILITY.sectionName:
        return ocularMotilityColumns;
      default:
        return { firstColumn: [], secondColumn: [], thirdColumn: [] };
    }
  }, [
    sectionName,
    colorVisionColumns,
    vfEstermanColumns,
    vfScreeningColumns,
    vfThresholdingColumns,
    visualAcuityColumns,
    rapdColumns,
    contrastSensitivityColumns,
    full120Columns,
    ocularMotilityColumns,
    superior36Columns,
    lensSimulationColumns,
    videoColumns,
    deviationCorrespondenceColumns,
    fusionVergenceColumns,
    stereopsisColumns,
  ]);

  // columns to display on smaller screens
  const mobileColumns = useMemo(
    () => [
      ...columns.firstColumn,
      ...columns.secondColumn,
      ...columns.thirdColumn,
    ],
    [columns]
  );

  const getColumnDisplay = useCallback(
    (column: {
      title: string;
      value: string | undefined;
      isBlueText?: boolean;
    }) => {
      return (
        <Typography key={column.title} fontSize="13px">
          <span style={{ fontWeight: 600 }}>{column.title}:</span>{" "}
          <span
            style={{
              color: Boolean(column.isBlueText) ? "#2563EB" : "#000",
            }}
          >
            {column.value}
          </span>
        </Typography>
      );
    },
    []
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
      }}
    >
      {smallerThanSm ? (
        <Grid container item xs={12}>
          <Grid item xs={6}>
            {mobileColumns
              ?.slice(0, Math.ceil(mobileColumns?.length / 2))
              .map((column) => getColumnDisplay(column))}
          </Grid>
          <Grid item xs={6}>
            {mobileColumns
              ?.slice(Math.ceil(mobileColumns?.length / 2))
              .map((column) => getColumnDisplay(column))}
          </Grid>
        </Grid>
      ) : (
        <Grid container item xs={12}>
          <Grid item xs={4}>
            {columns?.firstColumn?.map((column) => getColumnDisplay(column))}
          </Grid>
          <Grid item xs={4}>
            {columns?.secondColumn?.map((column) => getColumnDisplay(column))}
          </Grid>
          <Grid item xs={4}>
            {columns?.thirdColumn?.map((column) => getColumnDisplay(column))}
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default ReportColumns;
